import React, { useState, useEffect, useRef } from 'react';
import { uuidv4 } from '@graphistry/node-util/uuid';
import styles from './styles.less';
import { ICON_URIS } from './constants';

import { SetItem } from './collections-container.tsx';
import { SetModal } from '../set-editor/index.ts';

interface Encoding {
    type: string;
    color: string;
}

interface SetExpr {
    type?: string;
    sets?: string[];
    gfql?: any[];
    dataType?: string;
}

interface Set {
    id: string;
    name?: string;
    type?: string;
    description?: string;
    enabled?: boolean;
    node_encoding: Encoding;
    edge_encoding: Encoding;
    expr?: SetExpr;
}

interface Column {
    name: string;
    type?: string;
}

interface PanelPosition {
    left: string;
    transform: string;
}

interface UpdateCollectionSetsParams {
    value: Record<string, Set> | Set[];
    editIndex?: number;
}

interface UpdateEncodingParams {
    index: number;
    value: string | null;
    graphType: string;
}

interface UpdateNameParams {
    index: number;
    value: string;
}

interface ToggleCollectionsParams {
    value: boolean;
}

interface CollectionsComponentProps {
    sets: Record<string, Set> | Set[];
    visible: boolean;
    onUpdateCollectionSets: (params: UpdateCollectionSetsParams) => void;
    onUpdateCollectionEdgeEncoding?: (params: UpdateEncodingParams) => void;
    onUpdateCollectionNodeEncoding?: (params: UpdateEncodingParams) => void;
    onUpdateCollectionName?: (params: UpdateNameParams) => void;
    onToggleCollections: (params: ToggleCollectionsParams) => void;
    columns: Column[];
}

export const CollectionsComponent: React.FC<CollectionsComponentProps> = ({
    sets,
    visible,
    onUpdateCollectionSets,
    onUpdateCollectionEdgeEncoding,
    onUpdateCollectionNodeEncoding,
    onUpdateCollectionName,
    onToggleCollections,
    columns,
}) => {

    const setsArray: Set[] = sets ? Array.from({ length: (sets as any).length }, (_, i) => (sets as any)[i]).filter(Boolean) : [];

    const [draggedSetIndex, setDraggedSetIndex] = useState<number | null>(null);
    const [showIntersectionModal, setShowIntersectionModal] = useState(false);
    const [showSetModal, setShowSetModal] = useState(false);
    const [selectedSets, setSelectedSets] = useState<string[]>([]);
    const [intersectionName, setIntersectionName] = useState('');
    const [panelPosition, setPanelPosition] = useState<PanelPosition>({ left: '50%', transform: 'translateX(-50%) translateY(-30px)' });
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const calculatePosition = () => {
            const button = document.getElementById('toggle-collections');
            if (button && panelRef.current) {
                const buttonRect = button.getBoundingClientRect();
                const panelRect = panelRef.current.getBoundingClientRect();

                const buttonCenter = buttonRect.left + (buttonRect.width / 2);
                const panelLeft = buttonCenter - (panelRect.width / 2);

                setPanelPosition({
                    left: `${panelLeft}px`,
                    transform: 'translateY(-30px)'
                });
            }
        };

        if (visible) {
            setTimeout(calculatePosition, 0);

            window.addEventListener('resize', calculatePosition);

            return () => {
                window.removeEventListener('resize', calculatePosition);
            };
        }
    }, [visible]);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number): void => {
        setDraggedSetIndex(index);
        e.currentTarget.classList.add(styles['dragging']);
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
        e.currentTarget.classList.remove(styles['dragging']);
        e.currentTarget.style.opacity = '1';
        setDraggedSetIndex(null);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.currentTarget.style.borderBottom = '2px solid #3ca5c6';
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
        e.currentTarget.style.borderBottom = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number): void => {
        e.preventDefault();
        e.currentTarget.style.borderBottom = '';

        if (draggedSetIndex === null || draggedSetIndex === dropIndex) {
            return;
        }


        const newSetsArray = [...setsArray];
        const [draggedSet] = newSetsArray.splice(draggedSetIndex, 1);
        newSetsArray.splice(dropIndex, 0, draggedSet);

        onUpdateCollectionSets({value: newSetsArray.reduce((setObj: Record<string, Set>, set, i) => {
            setObj[String(i)] = set;
            return setObj;
        }, {})});
    };

    const togglePanel = (): void => {
        onToggleCollections({ value: !visible });
    };

    const getSetIcon = (type?: string): string => {
        switch (type) {
            case 'intersection':
                return ICON_URIS.INTERSECTION;
            case 'set':
                return ICON_URIS.SET;
            case 'global':
            default:
                return '';
        }
    };

    const handleDeleteSet = (index: number): void => {
        const newSetsArray = [...setsArray];
        newSetsArray.splice(index, 1);

        const newSetsObject = newSetsArray.reduce((obj: Record<string, Set>, set, i) => {
            obj[String(i)] = set;
            return obj;
        }, {});

        onUpdateCollectionSets({ value: newSetsObject });
    };

    const handleCreateIntersection = (): void => {
        if (selectedSets.length >= 2 && intersectionName.trim()) {
            const newSet: Set = {
                id: uuidv4(),
                name: intersectionName.trim(),
                description: '',
                type: 'intersection',
                enabled: true,
                node_encoding: {
                    type: 'passthrough',
                    color: '#FFFFFF'
                },
                edge_encoding: {
                    type: 'passthrough',
                    color: '#FFFFFF'
                },
                expr: {
                    type: 'intersection',
                    sets: selectedSets
                }
            };

            const newSetsArray = [newSet, ...setsArray];
            const newSetsObject = newSetsArray.reduce((obj: Record<string, Set>, set, i) => {
                obj[String(i)] = set;
                return obj;
            }, {});

            onUpdateCollectionSets({ value: newSetsObject });

            setShowIntersectionModal(false);
            setSelectedSets([]);
            setIntersectionName('');
        }
    };

    // TODO: replace with dedicated falcor handler
    const handleUpdateEncoding = ({ index, value, graphType }: UpdateEncodingParams): void => {
        const newSetsObject = [...setsArray].reduce((obj: Record<string, Set>, set, i) => {
            obj[String(i)] = set;
            return obj;
        }, {});
        const encodingType = graphType === 'p' ? 'node_encoding' : 'edge_encoding';
        newSetsObject[index][encodingType] = value === null
            ? { type: 'passthrough', color: newSetsObject[index][encodingType].color }
            : { type: 'fill', color: value };

        onUpdateCollectionSets({ value: newSetsObject });
    };

    // TODO: replace with dedicated falcor handler
    const handleUpdateName = ({ index, value }: UpdateNameParams): void => {
        const newSetsObject = [...setsArray].reduce((obj: Record<string, Set>, set, i) => {
            obj[String(i)] = set;
            return obj;
        }, {});
        newSetsObject[index].name = value;

        onUpdateCollectionSets({ value: newSetsObject });
    };

    const handleUpdateCollectionSets = ({ value, editIndex }: UpdateCollectionSetsParams): void => {
        if (editIndex !== undefined) {
            const updatedSets = [...setsArray];
            updatedSets[editIndex] = (value as Record<string, Set>)[editIndex];

            const newSetsObject = updatedSets.reduce((obj: Record<string, Set>, set, i) => {
                obj[String(i)] = set;
                return obj;
            }, {});

            onUpdateCollectionSets({ value: newSetsObject });
        } else {
            onUpdateCollectionSets({ value });
        }
    };

    const isEdgeGlobalPassthrough = setsArray.find(set => set.type === 'global')?.edge_encoding.type === 'passthrough';
    const isAllNonGlobalEdgesPassthrough = setsArray
        .filter(set => set?.type !== 'global')
        .every(set => set?.edge_encoding?.type === 'passthrough');

    return (
        <div className={styles['collections-wrapper']}>
            <button
                className={styles['collections-toggle']}
                onClick={togglePanel}
                title="Collections"
            >
                <img src={ICON_URIS.SET_LIGHT} alt="Collections" style={{ width: '18px', height: '18px' }} />
            </button>
            {visible && (
                <div
                    ref={panelRef}
                    className={styles['collections-content']}
                    style={panelPosition}
                >
                    <h2>Collections</h2>
                    <div className={styles['sets-container']}>
                        {setsArray.map((set, index) => set && (
                            <SetItem
                                key={set.id}
                                index={index}
                                set={set}
                                setIcon={getSetIcon(set.type)}
                                handleDragStart={handleDragStart}
                                handleDragEnd={handleDragEnd}
                                handleDragOver={handleDragOver}
                                handleDragLeave={handleDragLeave}
                                handleDrop={handleDrop}
                                // UpdateEncodingParams.index (number) vs EncodingChangeParams.index
                                // (string|number) — pre-existing cross-module drift ts3.9 allowed
                                onUpdateCollectionNodeEncoding={handleUpdateEncoding as any}
                                onUpdateCollectionEdgeEncoding={handleUpdateEncoding as any}
                                onUpdateCollectionName={handleUpdateName}
                                onUpdateCollectionSets={handleUpdateCollectionSets as any}
                                onDeleteCollection={handleDeleteSet}
                                isGlobal={set.type === 'global'}
                                isEdgeGlobalPassthrough={isEdgeGlobalPassthrough}
                                isAllNonGlobalEdgesPassthrough={isAllNonGlobalEdgesPassthrough}
                                columns={columns}
                                intersectionSetsNames={setsArray}
                            />
                        ))}
                    </div>

                    <div className={styles['collections-actions']}>
                        <button
                            className={styles['action-button']}
                            onClick={() => setShowSetModal(true)}
                        >
                            <img src={ICON_URIS.SET} alt="Set icon" />
                            <span>+ Add Collection</span>
                        </button>
                        <button
                            className={styles['action-button']}
                            onClick={() => setShowIntersectionModal(true)}
                        >
                            <img src={ICON_URIS.INTERSECTION} alt="Intersection icon" />
                            <span>+ Add Intersection</span>
                        </button>
                    </div>
                </div>
            )}

            {showIntersectionModal && (
                <div className={styles['modal-overlay']}>
                    <div className={styles['modal-content']}>
                        <h3>Create Intersection</h3>
                        <div className={styles['modal-body']}>
                            <input
                                type="text"
                                placeholder="Intersection Name"
                                value={intersectionName}
                                onChange={(e) => setIntersectionName(e.target.value)}
                                className={styles['intersection-name-input']}
                            />
                            <div className={styles['sets-select']}>
                                {setsArray.map((set) => (
                                    <label key={set.id} className={styles['set-option']}>
                                        <input
                                            type="checkbox"
                                            checked={selectedSets.includes(set.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedSets([...selectedSets, set.id]);
                                                } else {
                                                    setSelectedSets(selectedSets.filter(id => id !== set.id));
                                                }
                                            }}
                                        />
                                        <span>{set.name || `Set ${set.id}`}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className={styles['modal-footer']}>
                            <button
                                className={styles['cancel-button']}
                                onClick={() => {
                                    setShowIntersectionModal(false);
                                    setSelectedSets([]);
                                    setIntersectionName('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles['create-button']}
                                onClick={handleCreateIntersection}
                                disabled={selectedSets.length < 2 || !intersectionName.trim()}
                            >
                                Create Intersection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSetModal && <SetModal
                isOpen={showSetModal}
                onClose={() => setShowSetModal(false)}
                onConfirm={handleUpdateCollectionSets}
                // local Set/Column interfaces drifted from set-editor/types —
                // pre-existing mismatch surfaced by ts5 (ts3.9 allowed it)
                collections={{ sets, columns } as any}
                interactionDetails={{
                    type: 'gfql_chain',
                    componentType: 'node',
                    dataType: 'string',
                    name: '',
                    value: '',
                    columns: columns
                } as any}
            />}
        </div>
    );
};
